import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { timeTrackingService, caseService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDialog from '../components/common/ConfirmDialog';
import {
  Clock,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Calendar,
  FileText,
  Filter,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const TENTHS_PRESETS = [0.1, 0.2, 0.3, 0.5, 1.0, 2.0];

const formatHoursDecimal = (hours) => {
  const num = parseFloat(hours);
  return isNaN(num) ? '0.0' : num.toFixed(1);
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const TimeEntries = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);

  // Filters - default to current month
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      case_id: '',
      show_all: false, // false = my entries, true = all firm entries
      start_date: firstOfMonth.toISOString().slice(0, 10),
      end_date: now.toISOString().slice(0, 10)
    };
  });

  // Pagination
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 500
  });

  // Create entry form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    hours: '',
    description: '',
    case_id: '',
    is_billable: true,
    logged_at: new Date().toISOString().slice(0, 10)
  });
  const [submitting, setSubmitting] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  // Edit state
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editForm, setEditForm] = useState({
    hours: '',
    description: '',
    logged_at: ''
  });

  // Fetch cases for dropdown
  useEffect(() => {
    const fetchCases = async () => {
      try {
        const res = await caseService.getCases({ limit: 500, status: 'active' });
        setCases(res.data || []);
      } catch (err) {
        console.error('Error fetching cases:', err);
      }
    };
    fetchCases();
  }, []);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        limit: pagination.limit,
        offset: pagination.offset
      };
      if (filters.case_id) {
        params.case_id = filters.case_id;
      }
      // Server-side user filtering
      if (!filters.show_all && user) {
        params.user_id = user.id;
      }
      // Date range filtering
      if (filters.start_date) {
        params.start_date = filters.start_date;
      }
      if (filters.end_date) {
        params.end_date = filters.end_date;
      }
      const res = await timeTrackingService.listEntries(params);
      setEntries(res.data || []);
    } catch (err) {
      console.error('Error fetching time entries:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, user]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleCreateEntry = async () => {
    const hours = parseFloat(createForm.hours);
    if (!hours || hours <= 0) {
      return;
    }
    try {
      setSubmitting(true);
      await timeTrackingService.createEntry({
        hours,
        description: createForm.description || null,
        case_id: createForm.case_id || null,
        is_billable: createForm.is_billable,
        logged_at: new Date(createForm.logged_at + 'T12:00:00').toISOString()
      });
      setCreateForm({
        hours: '',
        description: '',
        case_id: '',
        is_billable: true,
        logged_at: new Date().toISOString().slice(0, 10)
      });
      setShowCreateForm(false);
      fetchEntries();
    } catch (err) {
      console.error('Error creating time entry:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = (entry) => {
    setEditingEntryId(entry.id);
    setEditForm({
      hours: parseFloat(entry.hours).toFixed(1),
      description: entry.description || '',
      logged_at: new Date(entry.logged_at).toISOString().slice(0, 10),
      is_billable: entry.is_billable
    });
  };

  const cancelEditing = () => {
    setEditingEntryId(null);
    setEditForm({ hours: '', description: '', logged_at: '', is_billable: true });
  };

  const handleSaveEdit = async () => {
    const hours = parseFloat(editForm.hours);
    if (!hours || hours <= 0) {
      return;
    }
    try {
      setSubmitting(true);
      await timeTrackingService.updateEntry(editingEntryId, {
        hours,
        description: editForm.description || null,
        logged_at: new Date(editForm.logged_at + 'T12:00:00').toISOString(),
        is_billable: editForm.is_billable
      });
      setEditingEntryId(null);
      fetchEntries();
    } catch (err) {
      console.error('Error updating time entry:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = (entryId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Time Entry',
      message: 'Are you sure you want to delete this time entry?',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await timeTrackingService.deleteEntry(entryId);
          fetchEntries();
        } catch (err) {
          console.error('Error deleting time entry:', err);
        }
      },
    });
  };

  const totalHours = entries.reduce((sum, e) => sum + parseFloat(e.hours), 0);
  const billableHours = entries.filter(e => e.is_billable).reduce((sum, e) => sum + parseFloat(e.hours), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Clock className="w-8 h-8 mr-3 text-yellow-600" />
              Time Entries
            </h1>
            <p className="text-gray-600 mt-1">Track and manage billable time</p>
          </div>

          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-600 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Log Time
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 uppercase">Total Entries</p>
            <p className="text-2xl font-bold text-blue-700">{entries.length}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 uppercase">Total Hours</p>
            <p className="text-2xl font-bold text-green-700">{formatHoursDecimal(totalHours)}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 uppercase">Billable Hours</p>
            <p className="text-2xl font-bold text-yellow-700">{formatHoursDecimal(billableHours)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setFilters(f => ({ ...f, show_all: false }));
                setPagination(p => ({ ...p, offset: 0 }));
              }}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                !filters.show_all
                  ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                  : 'text-gray-600 hover:bg-gray-100 border border-transparent'
              }`}
            >
              My Entries
            </button>
            <button
              onClick={() => {
                setFilters(f => ({ ...f, show_all: true }));
                setPagination(p => ({ ...p, offset: 0 }));
              }}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                filters.show_all
                  ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                  : 'text-gray-600 hover:bg-gray-100 border border-transparent'
              }`}
            >
              All Entries
            </button>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => {
                setFilters(f => ({ ...f, start_date: e.target.value }));
                setPagination(p => ({ ...p, offset: 0 }));
              }}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => {
                setFilters(f => ({ ...f, end_date: e.target.value }));
                setPagination(p => ({ ...p, offset: 0 }));
              }}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
          </div>

          {/* Case Filter */}
          <select
            value={filters.case_id}
            onChange={(e) => {
              setFilters(f => ({ ...f, case_id: e.target.value }));
              setPagination(p => ({ ...p, offset: 0 }));
            }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            <option value="">All Cases</option>
            {cases.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>

          {(filters.case_id || filters.start_date || filters.end_date) && (
            <button
              onClick={() => {
                const now = new Date();
                const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                setFilters(f => ({
                  ...f,
                  case_id: '',
                  start_date: firstOfMonth.toISOString().slice(0, 10),
                  end_date: now.toISOString().slice(0, 10)
                }));
                setPagination(p => ({ ...p, offset: 0 }));
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Reset filters
            </button>
          )}
        </div>
      </div>

      {/* Create Entry Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Log Time Entry</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={createForm.hours}
                  onChange={(e) => setCreateForm(f => ({ ...f, hours: e.target.value }))}
                  className="w-24 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="0.0"
                  autoFocus
                />
                <div className="flex flex-wrap gap-1">
                  {TENTHS_PRESETS.map(val => (
                    <button
                      key={val}
                      onClick={() => setCreateForm(f => ({ ...f, hours: String(val) }))}
                      className={`px-2 py-1 rounded text-xs border ${
                        createForm.hours === String(val)
                          ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {val}h
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Case</label>
              <select
                value={createForm.case_id}
                onChange={(e) => setCreateForm(f => ({ ...f, case_id: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="">No case (unlinked)</option>
                {cases.map(c => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={createForm.description}
                onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="What did you work on?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={createForm.logged_at}
                  onChange={(e) => setCreateForm(f => ({ ...f, logged_at: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={createForm.is_billable}
                onChange={(e) => setCreateForm(f => ({ ...f, is_billable: e.target.checked }))}
                className="rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
              />
              Billable
            </label>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={handleCreateEntry}
              disabled={submitting || !createForm.hours}
              className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-600 disabled:opacity-50 flex items-center"
            >
              {submitting ? 'Saving...' : 'Save Entry'}
            </button>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setCreateForm({
                  hours: '',
                  description: '',
                  case_id: '',
                  is_billable: true,
                  logged_at: new Date().toISOString().slice(0, 10)
                });
              }}
              className="text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Entries Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500">No time entries found</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="mt-4 text-yellow-600 hover:text-yellow-800 font-medium"
            >
              Log your first time entry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoiced</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {entries.map(entry => (
                  editingEntryId === entry.id ? (
                    <tr key={entry.id} className="bg-yellow-50">
                      <td className="px-4 py-3">
                        <input
                          type="date"
                          value={editForm.logged_at}
                          onChange={(e) => setEditForm(f => ({ ...f, logged_at: e.target.value }))}
                          className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="0.1"
                            step="0.1"
                            value={editForm.hours}
                            onChange={(e) => setEditForm(f => ({ ...f, hours: e.target.value }))}
                            className="w-16 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                          <div className="flex gap-0.5">
                            {[0.1, 0.5, 1.0].map(val => (
                              <button
                                key={val}
                                onClick={() => setEditForm(f => ({ ...f, hours: String(val) }))}
                                className="px-1.5 py-0.5 rounded text-xs bg-gray-100 hover:bg-gray-200 text-gray-600"
                              >
                                {val}
                              </button>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3" colSpan={2}>
                        <input
                          type="text"
                          value={editForm.description}
                          onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          placeholder="Description"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{entry.user_name}</td>
                      <td className="px-4 py-3">
                        <select
                          value={editForm.is_billable ? 'billable' : 'non-billable'}
                          onChange={(e) => setEditForm(f => ({ ...f, is_billable: e.target.value === 'billable' }))}
                          className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="billable">Billable</option>
                          <option value="non-billable">Non-billable</option>
                        </select>
                      </td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={handleSaveEdit}
                            disabled={submitting}
                            className="p-1.5 rounded bg-yellow-500 text-white hover:bg-yellow-600"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="p-1.5 rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatDate(entry.logged_at)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {formatHoursDecimal(entry.hours)}h
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {entry.description || <span className="text-gray-400 italic">No description</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {entry.case_id ? (
                          <Link
                            to={`/cases/${entry.case_id}`}
                            className="text-yellow-600 hover:text-yellow-800 flex items-center gap-1"
                          >
                            <FileText className="w-3 h-3" />
                            {entry.case_title || 'View Case'}
                          </Link>
                        ) : (
                          <span className="text-gray-400">Unlinked</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {entry.user_name}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-1">
                          {entry.is_billable ? (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                              Billable
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                              Non-billable
                            </span>
                          )}
                          {entry.is_billed && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                              Billed
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {entry.is_billed ? (
                          <span className="text-green-700 font-medium">Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!entry.is_billed && (
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => startEditing(entry)}
                              className="p-1.5 rounded text-gray-400 hover:text-yellow-600 hover:bg-gray-100"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEntry(entry.id)}
                              className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-gray-100"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {entries.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {pagination.offset + 1} - {pagination.offset + entries.length} entries
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
                disabled={pagination.offset === 0}
                className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPagination(p => ({ ...p, offset: p.offset + p.limit }))}
                disabled={entries.length < pagination.limit}
                className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  );
};

export default TimeEntries;
